import { useCallback, useEffect, useMemo, useState } from 'react';

import { useAppSelector } from '@/configuration/setup/hooks';
import { getAssetTreeAccountIds, getAssetTreeAssetIds, getAssetTreeWorkshopIds } from '@/features/app/assetTreeSlice';
import { useGetVehicleStatusDataQuery } from '@/services/vehicleStatusApi';
import { removeUndefinedOrEmptyArrayAttributes } from '@/utils/commonUtils';
import useSessionStorageState from '@/utils/useSessionStorageState';
import {
    sanitizeVehicleStatusDataSections,
    VehicleStatusDataSectionKey,
    VehicleStatusDataSections,
} from './DataTabsSettingsDialogComponent';
import { Criticality, SyncStatus } from './FilterComponent';
import useShouldSkipQuery from './useShouldSkipQuery';

const INITIAL_PAGE_NUMBER = 1;
const INITIAL_PAGE_SIZE = 60;

const vehicleStatusDataSections: VehicleStatusDataSectionKey[] = Object.values(VehicleStatusDataSections);

const useFetchVehiclesStatusData = () => {
    const [criticalityStatus, setCriticalityStatus] = useSessionStorageState<Criticality[]>(
        [],
        'vs-criticality-status',
    );
    const [syncStatus, setSyncStatus] = useSessionStorageState<SyncStatus[]>([], 'vs-sync-status');
    const [pageNumber, setPageNumber] = useState<number>(INITIAL_PAGE_NUMBER);
    const [selectedAssetId, setSelectedAssetId] = useState<string | undefined>(undefined);
    const [pageSize, setPageSize] = useSessionStorageState<number>(INITIAL_PAGE_SIZE, 'vs-page-size');
    const [storedDataTabsSections, setSelectedDataTabsSections] = useSessionStorageState<VehicleStatusDataSectionKey[]>(
        vehicleStatusDataSections,
        'vs-data-tabs-settings',
    );
    const selectedDataTabsSections = useMemo(
        () => sanitizeVehicleStatusDataSections(storedDataTabsSections),
        [storedDataTabsSections],
    );

    // get values from asset tree
    const assetIds = useAppSelector(getAssetTreeAssetIds);
    const accountIds = useAppSelector(getAssetTreeAccountIds);
    const workshopIds = useAppSelector(getAssetTreeWorkshopIds);

    const { shouldSkip, oldLocationId, newLocationId } = useShouldSkipQuery();

    const {
        data = {
            page_number: pageNumber,
            page_size: pageSize,
            total_pages: 0,
            is_csv_export_allowed: false,
            items: [],
        },
        isLoading,
        error,
        refetch,
    } = useGetVehicleStatusDataQuery(
        removeUndefinedOrEmptyArrayAttributes({
            asset_ids: assetIds,
            account_ids: accountIds,
            workshop_ids: workshopIds,
            status: criticalityStatus,
            sync_status: syncStatus,
            page_number: pageNumber,
            page_size: pageSize,
            sections_to_include: selectedDataTabsSections,
        }),
        { skip: shouldSkip },
    );

    const fetchVehicleStatusData = useCallback(() => {
        refetch().then((response) => {
            setSelectedAssetId(undefined);
            scrollToTop();
        });
    }, [refetch]);

    const changePage = useCallback((page: number) => {
        setPageNumber(page);
        setSelectedAssetId(undefined);
        scrollToTop();
    }, []);

    const scrollToTop = () => {
        document.documentElement.scrollIntoView({
            behavior: 'smooth',
        });
    };

    useEffect(() => {
        // This useEffect forces pageNumber to 1 whenever one of the dependencies changes
        // This also means that on any dependency change two queries will be triggered back to back
        setPageNumber(INITIAL_PAGE_NUMBER);
        setSelectedAssetId(undefined);
        scrollToTop();
    }, [assetIds, accountIds, workshopIds, criticalityStatus, syncStatus, pageSize, selectedDataTabsSections]);

    useEffect(() => {
        if (oldLocationId === 'DETAIL_VIEW' || newLocationId !== 'VEHICLE_STATUS') return;

        // Go to page 1 and reload if user doesn't come from detail view
        setPageNumber(INITIAL_PAGE_NUMBER);
        setSelectedAssetId(undefined);
        scrollToTop();
    }, [newLocationId, oldLocationId]);

    return {
        isLoading,
        hasErrors: !!error,
        vehiclesStatusData: data.items,
        pageSize: data.page_size,
        pageNumber: data.page_number,
        totalPages: data.total_pages,
        isCsvExportAllowed: data.is_csv_export_allowed,
        triggerReload: fetchVehicleStatusData,
        changeCriticalityStatus: setCriticalityStatus,
        changeSyncStatus: setSyncStatus,
        changePageNumber: changePage,
        changePageSize: setPageSize,
        changeSelectedDataTabSections: setSelectedDataTabsSections,
        selectedDataTabsSections,
        criticalityStatus,
        syncStatus,
        setSelectedAssetId,
        highlightAssetId: oldLocationId === 'DETAIL_VIEW' ? selectedAssetId : undefined,
        clearHighlight: () => setSelectedAssetId(undefined),
    };
};

export default useFetchVehiclesStatusData;
